import { PageHeader } from '@/components/integrations/wix/PageHeader'
import { WixConfigForm } from '@/components/integrations/wix/WixConfigForm'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { toast } from 'sonner'
import z from 'zod'

export const Route = createFileRoute('/_dashboard/settings/integrations/wix')({
  component: RouteComponent,
  validateSearch: z.object({
    code: z.string().optional(),
    instanceId: z.string().optional(),
  }),
  beforeLoad: async ({ search }) => {
    const code = search.code
    const instanceId = search.instanceId
    if (code && instanceId) {
      await orpc.integrations.wix.exchangeCodeForWixApiKey.call({
        code,
        instanceId,
      })
      throw redirect({
        to: '/settings/integrations/wix',
        search: {},
        replace: true,
      })
    }
    await queryClient.ensureQueryData(
      orpc.integrations.getIntegrations.queryOptions(),
    )
    await queryClient.ensureQueryData(
      orpc.integrations.wix.getWixSiteInfo.queryOptions(),
    )
  },
})

function RouteComponent() {
  const integrationsQuery = useQuery(
    orpc.integrations.getIntegrations.queryOptions({
      select: (data) => {
        const wixIntegration = data.find((integration) => {
          return integration.config.type === 'wix'
        })
        if (wixIntegration?.config.type === 'wix') {
          return {
            ...wixIntegration,
            config: wixIntegration.config,
          }
        }
        return null
      },
    }),
  )

  const toggleEnabledMutation = useMutation(
    orpc.integrations.toggleIntegrationEnabled.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.getIntegrations.queryKey(),
        })
        toast.success('Integration updated successfully')
      },
      onError: () => {
        toast.error('Failed to update integration')
      },
    }),
  )

  const handleToggleEnabled = (checked: boolean) => {
    if (!integrationsQuery.data?.id) return
    toggleEnabledMutation.mutate({
      integrationId: integrationsQuery.data.id,
      enabled: checked,
    })
  }

  const isWixConnected = !!integrationsQuery.data

  return (
    <div className="max-w-4xl">
      <PageHeader />

      {isWixConnected && <div className="mb-6" />}

      <WixConfigForm
        integrationEnabled={integrationsQuery.data?.enabled ?? false}
        onToggleEnabled={handleToggleEnabled}
        isTogglingEnabled={toggleEnabledMutation.isPending}
      />
    </div>
  )
}
